/**
 * Following a presigned download, in one place.
 *
 * Not a plain `<a href>` to the API: it is another origin and the session is an
 * httpOnly cookie, so every download is ask-for-a-signed-URL then go to it.
 *
 * We do NOT open a new tab. Opening `about:blank` and pointing it at the signed
 * URL left that tab stranded on `about:blank` once the download started — the
 * bug this replaces. The API now presigns with `Content-Disposition:
 * attachment`, so a hidden same-document anchor click downloads the file WITHOUT
 * navigating this app away and WITHOUT a leftover blank tab. `rel=noopener` and
 * the immediate removal keep it from leaking anything.
 */
import apiClient from './index';

interface DownloadTicket {
	url: string;
	expiresInSeconds: number;
}

export async function followPresigned(path: string): Promise<void> {
	const response = await apiClient.get<DownloadTicket>(path);
	followUrl(response.data.url);
}

/** Like `followPresigned`, for a ticket that is asked for with a POST body
 *  (a selective export): send `body`, then follow the signed URL it answers. */
export async function followPresignedPost(
	path: string,
	body: unknown,
): Promise<void> {
	const response = await apiClient.post<DownloadTicket>(path, body);
	followUrl(response.data.url);
}

/** Go to an already-signed URL without leaving the app. `filename` is only a
 *  hint: a cross-origin link keeps the server's own `attachment` name. */
export function followUrl(url: string, filename = ''): void {
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.rel = 'noopener';
	anchor.download = filename;
	anchor.style.display = 'none';
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
}
